/*
 * Questions section for CreateEditIntakeView: the `intakeQuestionBuildersList.map`
 * over the Task-6 CreateNewIntakeFormQuestionsBuilder leaves, plus the trailing
 * NewQuestionBtnTool when all builders are closed.
 *
 * The Task-6 leaf is wired with its EXISTING prop names/shapes unchanged — including
 * the props it does not internally consume (fileFormatOptions, onChangeFileFormatValue,
 * questionTypeLocalError, fileFormatError, fileFormatLocalError, onNewQuestionBtnToolPress,
 * isLastInList) — that pass-through is pre-existing and intentionally preserved.
 *
 * `onPageCancel` is forwarded only in create mode (matches the giant: the create branch
 * passed onPageCancel={onCancelBtnPress}; the edit branch did not pass it at all).
 *
 * SCSS → Tailwind mapping:
 *   .ceiv-section-title: font-size:16px / font-weight:600 / color:--text-dark / margin:0 0 12px
 *     → text-size-4 font-semibold text-text-dark m-0 mb-sp-1.5
 *   .ceiv-questions-body: flex / flex-direction:column / gap:12px → flex flex-col gap-sp-1.5
 */
import { CreateNewIntakeFormQuestionsBuilder } from '../../CreateNewIntakeFormQuestionsBuilder/CreateNewIntakeFormQuestionsBuilder'
import { NewQuestionBtnTool } from '../../NewQuestionBtnTool/NewQuestionBtnTool'
import type { IntakeQuestionBuilder, FileFormatOption, QuestionOption } from '../types'

type Props = {
  intakeQuestionBuildersList: IntakeQuestionBuilder[]
  isAllBuildersClosed: boolean
  onToggleFollowUp: (builderId: string, optionId: string) => void
  onChangeFollowUpRequired: (builderId: string, optionId: string, value: boolean) => void
  onChangeFollowUpText: (builderId: string, optionId: string, value: string) => void
  onChangeQuestionTypeValue: (builderId: string, value: string) => void
  onChangeFileFormatValue: (builderId: string, value: FileFormatOption[]) => void
  onChangeQuestionOptionValue: (builderId: string, optionId: string, value: string) => void
  onChangeQuestionOptionErrorValue: (
    builderId: string,
    optionId: string,
    errorText: string,
    isClearOptionValue?: boolean,
  ) => void
  onChangeQuestionMessageValue: (builderId: string, value: string) => void
  onChangeCheckBoxesValue: (builderId: string, checkBoxType: string, value: boolean) => void
  onBuilderCancelBtnPress: (builderId: string) => void
  previouslySavedQuestionCancelBtnPress: (builderId: string) => void
  onNewQuestionBtnToolPress: (questionListLength: number) => void
  onAddOptionIconPress: (initiatorIndex: number, builderId: string) => void
  onBuilderSaveBtnPress: (
    questionTypeValue: string,
    questionMessage: string,
    builderId: string,
    questionOptionsList: QuestionOption[],
    isRequired: boolean,
  ) => void
  onEditSavedQuestion: (builderId: string) => void
  onDeleteQuestion: (builderId: string) => void
  onDuplicateQuestion: (builderId: string) => void
  /** Forwarded only in create mode (undefined in edit mode), matching the giant. */
  onPageCancel?: (() => void) | undefined
}

export function QuestionBuildersList({
  intakeQuestionBuildersList,
  isAllBuildersClosed,
  onToggleFollowUp,
  onChangeFollowUpRequired,
  onChangeFollowUpText,
  onChangeQuestionTypeValue,
  onChangeFileFormatValue,
  onChangeQuestionOptionValue,
  onChangeQuestionOptionErrorValue,
  onChangeQuestionMessageValue,
  onChangeCheckBoxesValue,
  onBuilderCancelBtnPress,
  previouslySavedQuestionCancelBtnPress,
  onNewQuestionBtnToolPress,
  onAddOptionIconPress,
  onBuilderSaveBtnPress,
  onEditSavedQuestion,
  onDeleteQuestion,
  onDuplicateQuestion,
  onPageCancel,
}: Props) {
  return (
    <div>
      <h2 className='m-0 mb-sp-1.5 text-size-4 font-semibold text-text-dark'>Questions</h2>
      <div className='flex flex-col gap-sp-1.5'>
        {intakeQuestionBuildersList.map((t, i) => (
          <CreateNewIntakeFormQuestionsBuilder
            key={t.id}
            questionNumber={i + 1}
            onToggleFollowUp={onToggleFollowUp}
            onChangeFollowUpRequired={onChangeFollowUpRequired}
            onChangeFollowUpText={onChangeFollowUpText}
            builderId={t.id}
            questionTypeValue={t.questionTypeValue}
            fileFormatError={t.fileFormatError ?? false}
            fileFormatLocalError={t.fileFormatLocalError ?? ''}
            // The leaf's FileFormatOption.allowedFormats is mutable string[] while ours is
            // readonly (to stay assignable from the edit-mode view-mode shape). The leaf does
            // not internally consume fileFormatOptions, so a mutable copy is behavior-equivalent.
            fileFormatOptions={t.fileFormatOptions.map((o) => ({
              ...o,
              allowedFormats: [...o.allowedFormats],
            }))}
            questionCheckBoxMultipleAnswersValue={t.checkboxesValues.EnableMultipleAnswers}
            questionOptionsList={t.options}
            onChangeQuestionTypeValue={onChangeQuestionTypeValue}
            onChangeFileFormatValue={onChangeFileFormatValue}
            onChangeQuestionOptionValue={onChangeQuestionOptionValue}
            onChangeQuestionOptionErrorValue={onChangeQuestionOptionErrorValue}
            onChangeQuestionMessageValue={onChangeQuestionMessageValue}
            onChangeCheckboxesValue={onChangeCheckBoxesValue}
            questionTypeError={t.questionTypeError ?? false}
            questionTypeLocalError={t.questionTypeLocalError ?? ''}
            questionMessageValueError={t.questionMessageValueError ?? false}
            questionMessageValueLocalError={t.questionMessageValueLocalError ?? ''}
            isOptionsValuesError={!t.options.every((ta) => !ta.isOptionError)}
            onCancelBtnPress={onBuilderCancelBtnPress}
            previouslySavedQuestionCancelBtnPress={previouslySavedQuestionCancelBtnPress}
            onNewQuestionBtnToolPress={onNewQuestionBtnToolPress}
            onAddOptionIconPress={onAddOptionIconPress}
            onSaveBtnPress={onBuilderSaveBtnPress}
            onEditSavedQuestion={onEditSavedQuestion}
            onDeleteQuestion={onDeleteQuestion}
            onDuplicateQuestion={onDuplicateQuestion}
            questionMessage={t.message}
            isPreviouslySaved={t.isPreviouslySaved}
            isSaved={t.isSaved ?? false}
            isRequiredValue={t.checkboxesValues.isRequired}
            previouslyIsRequiredValue={t?.previousSavedData?.checkboxesValues?.isRequired ?? false}
            onPageCancel={onPageCancel}
          />
        ))}
        {isAllBuildersClosed ?
          <NewQuestionBtnTool
            questionListLength={intakeQuestionBuildersList.length}
            onNewQuestionPress={onNewQuestionBtnToolPress}
          />
        : null}
      </div>
    </div>
  )
}
